import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import './Login.css';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotStep, setForgotStep] = useState(1);
  const [forgotData, setForgotData] = useState({
    email: '',
    employeeId: '',
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [verifiedUser, setVerifiedUser] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please enter email and password');
      return;
    }
    
    setError('');
    setLoading(true);

    try {
      console.log('Attempting login with email:', email);
      const result = await login(email, password);
      
      if (result.success) {
        console.log('Login successful!');
        navigate('/');
      } else {
        console.log('Login failed:', result.message);
        setError(result.message || 'Login failed. Please try again.');
        setLoading(false);
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('An error occurred during login. Please check your connection.');
      setLoading(false);
    }
  };

  const handleVerifyUser = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post('/auth/verify-user', {
        email: forgotData.email,
        employeeId: forgotData.employeeId,
        oldPassword: forgotData.oldPassword
      });

      if (response.data.success) {
        setVerifiedUser(response.data.userName);
        setForgotStep(2);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (forgotData.newPassword !== forgotData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (forgotData.newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/auth/forgot-password', forgotData);

      if (response.data.success) {
        setSuccess('Password changed successfully! You can now login.');
        setShowForgotPassword(false);
        setForgotStep(1);
        setForgotData({ email: '', employeeId: '', oldPassword: '', newPassword: '', confirmPassword: '' });
        setVerifiedUser('');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Password reset failed');
    } finally {
      setLoading(false);
    }
  };

  const closeForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotStep(1);
    setForgotData({ email: '', employeeId: '', oldPassword: '', newPassword: '', confirmPassword: '' });
    setVerifiedUser('');
    setError('');
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <h1>HRMS</h1>
        <h2>Login to your account</h2>
        
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success" style={{ background: '#d4edda', color: '#155724', padding: '10px', borderRadius: '5px', marginBottom: '15px' }}>{success}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              className="form-control"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter your email"
            />
          </div>
          
          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              className="form-control"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter your password"
            />
          </div>
          
          <button 
            type="submit" 
            className="btn btn-primary btn-block"
            disabled={loading}
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: '15px' }}>
          <button
            type="button"
            onClick={() => setShowForgotPassword(true)}
            style={{
              background: 'none',
              border: 'none',
              color: '#667eea',
              cursor: 'pointer',
              fontSize: '14px',
              textDecoration: 'underline'
            }}
          >
            🔑 Forgot Password?
          </button>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={closeForgotPassword}
        >
          <div 
            style={{
              background: '#fff',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '420px',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: '24px',
              color: '#fff'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: '22px' }}>🔐 Forgot Password</h2>
                  <p style={{ margin: '5px 0 0 0', opacity: 0.9, fontSize: '14px' }}>
                    {forgotStep === 1 ? 'Step 1: Verify with current password' : 'Step 2: Set new password'}
                  </p>
                </div>
                <button
                  onClick={closeForgotPassword}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    color: '#fff',
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    fontSize: '20px'
                  }}
                >
                  ×
                </button>
              </div>
              
              {/* Progress indicator */}
              <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                <div style={{
                  flex: 1,
                  height: '4px',
                  borderRadius: '2px',
                  background: '#fff'
                }} />
                <div style={{
                  flex: 1,
                  height: '4px',
                  borderRadius: '2px',
                  background: forgotStep === 2 ? '#fff' : 'rgba(255,255,255,0.3)'
                }} />
              </div>
            </div>

            {/* Body */}
            <div style={{ padding: '24px' }}>
              {error && (
                <div style={{
                  background: '#fee2e2',
                  color: '#dc2626',
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  fontSize: '14px'
                }}>
                  ⚠️ {error}
                </div>
              )}

              {forgotStep === 1 ? (
                <form onSubmit={handleVerifyUser}>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#334155' }}>
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={forgotData.email}
                      onChange={(e) => setForgotData({ ...forgotData, email: e.target.value })}
                      placeholder="Enter your registered email"
                      required
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e2e8f0',
                        borderRadius: '10px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#334155' }}>
                      Employee ID
                    </label>
                    <input
                      type="text"
                      value={forgotData.employeeId}
                      onChange={(e) => setForgotData({ ...forgotData, employeeId: e.target.value })}
                      placeholder="Enter your Employee ID"
                      required
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e2e8f0',
                        borderRadius: '10px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#334155' }}>
                      Current Password
                    </label>
                    <input
                      type="password"
                      value={forgotData.oldPassword}
                      onChange={(e) => setForgotData({ ...forgotData, oldPassword: e.target.value })}
                      placeholder="Enter your current password"
                      required
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e2e8f0',
                        borderRadius: '10px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '14px',
                      border: 'none',
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: '#fff',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.7 : 1
                    }}
                  >
                    {loading ? '⏳ Verifying...' : '✓ Verify & Continue'}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleResetPassword}>
                  <div style={{
                    background: '#e8f5e9',
                    padding: '12px',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    textAlign: 'center'
                  }}>
                    <span style={{ fontSize: '24px' }}>👋</span>
                    <p style={{ margin: '8px 0 0 0', color: '#2e7d32', fontWeight: '600' }}>
                      Hello, {verifiedUser}!
                    </p>
                    <p style={{ margin: '4px 0 0 0', color: '#388e3c', fontSize: '13px' }}>
                      Now set your new password
                    </p>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#334155' }}>
                      New Password
                    </label>
                    <input
                      type="password"
                      value={forgotData.newPassword}
                      onChange={(e) => setForgotData({ ...forgotData, newPassword: e.target.value })}
                      placeholder="Enter new password (min 6 chars)"
                      required
                      minLength={6}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e2e8f0',
                        borderRadius: '10px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#334155' }}>
                      Confirm Password
                    </label>
                    <input
                      type="password"
                      value={forgotData.confirmPassword}
                      onChange={(e) => setForgotData({ ...forgotData, confirmPassword: e.target.value })}
                      placeholder="Confirm new password"
                      required
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e2e8f0',
                        borderRadius: '10px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      type="button"
                      onClick={() => setForgotStep(1)}
                      style={{
                        flex: 1,
                        padding: '14px',
                        border: '2px solid #e2e8f0',
                        borderRadius: '10px',
                        background: '#fff',
                        color: '#64748b',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      ← Back
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      style={{
                        flex: 2,
                        padding: '14px',
                        border: 'none',
                        borderRadius: '10px',
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: '#fff',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        opacity: loading ? 0.7 : 1
                      }}
                    >
                      {loading ? '⏳ Resetting...' : '🔐 Reset Password'}
                    </button>
                  </div>
                </form>
              )}

              <p style={{
                textAlign: 'center',
                marginTop: '16px',
                fontSize: '13px',
                color: '#94a3b8'
              }}>
                💡 Contact HR if you don't remember your Employee ID
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;
